import React from 'react'
import styled, { keyframes } from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'

import Link from 'components/lib/DesignSystem/Link'

import EmptyStateIcon from './icons/EmptyStateIcon'

/* min-height, not height: a fixed viewport-derived height can end up shorter than the
   content on a low window, and a centred flex child then overflows equally above and
   below, straight into the filter row. min-height keeps the vertical centring on tall
   screens and lets the block grow on short ones. */
const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: calc(100vh - 387px);

  /* New nav: the shell (top bar, global banner, page inset) replaces the legacy 64px header. */
  .platform-nav-content & {
    min-height: calc(
      100vh - var(--top-bar-height, 52px) - var(--banner-height, 0px) -
        (var(--page-inset, 20px) * 2) - 350px
    );
  }
  padding: var(--t-spacing-8) 0;
`

/* Wide enough for the description to sit on four lines, broken at clause boundaries below,
   rather than six ragged ones. */
const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 560px;
`

/* MOTION (TBD proposal): the only empty state in the product that says "let's start", so
   its sparkles twinkle once on arrival, 60ms apart, after the illustration has landed. Plays
   once per mount; reduced motion renders final. */
const twinkle = keyframes`
  from {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    opacity: 1;
    transform: scale(1.15);
  }
`

const EmptyStateIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .sparkle {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${twinkle} 600ms cubic-bezier(0.33, 0, 0.2, 1) backwards;
  }

  .sparkle-big {
    animation-delay: 300ms;
  }

  @media (prefers-reduced-motion: reduce) {
    .sparkle {
      animation: none;
    }
  }
`

const EmptyStateTitle = styled.div`
  font-size: var(--t-typography-font-size-base);
  font-weight: var(--t-typography-font-weight-semi-bold);
  color: var(--color-text);
  margin: var(--t-spacing-4) 0 var(--t-spacing-2);
`

const EmptyStateDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-3);
  color: var(--sub-text-color);
  font-size: var(--t-typography-font-size-xs);
  line-height: var(--t-typography-line-height-xs);
  margin-bottom: var(--t-spacing-6);
`

const EmptyStateDescriptionValue = styled.span`
  font-weight: var(--t-typography-font-weight-bold);
`

const EmptyState = () => (
  <EmptyStateContainer>
    <EmptyStateContent>
      <EmptyStateIconContainer>
        <EmptyStateIcon />
      </EmptyStateIconContainer>
      <EmptyStateTitle>No simulations launched yet</EmptyStateTitle>
      <EmptyStateDescription>
        <div>
          Once a simulation goes out, it appears here with its click and report rates,
          <br />
          and who still needs training.
        </div>
        <div>
          The agent keeps a set of{' '}
          <EmptyStateDescriptionValue>suggested simulations</EmptyStateDescriptionValue> ready on
          the dashboard,
          <br />
          based on what it found about your organization.
        </div>
      </EmptyStateDescription>
    </EmptyStateContent>
    <Link href={URL_CONST.psima.self}>Review suggested simulations</Link>
  </EmptyStateContainer>
)

export default EmptyState
